import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HandlerMessageService } from 'app/common/services/handler-error/handler-message.service';
import { LanguageService } from 'app/core/i18n/language.service';
import { TranslatePipe } from 'app/core/i18n/translate.pipe';
import { BusinessPartnerAddressService } from 'app/modules/components/business-partners/business-partner/business-partner-address-modal/business-partner-address.component.service';
import { ConfirmationModalType } from 'app/shared/components/confirmation-modal';
import { CellRendererParams } from 'app/shared/components/smart-grid/core/interfaces/cell-renderer.interface';
import { ConfirmationService } from 'app/shared/services/confirmation.service';
import { take } from 'rxjs';
import { BusinessPartnerBySubRoute } from '../../../../interfaces/busines-partner-by-sub-route.interface';

@Component({
  selector: 'app-repin-cell-renderer',
  standalone: true,
  imports: [CommonModule, MatSlideToggleModule, MatTooltipModule, TranslatePipe],
  template: `
    <mat-slide-toggle
      [checked]="isRepinChecked()"
      (change)="onRepinToggle($event)"
      color="primary"
      [matTooltip]="'routing.SUB_ROUTE_PARTNERS.CELLS.REPIN_TOOLTIP' | t"
    />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RepinCellRendererComponent {
  private confirmationService = inject(ConfirmationService);
  private handlerMessage = inject(HandlerMessageService);
  private bpAddressService = inject(BusinessPartnerAddressService);
  private i18n = inject(LanguageService);

  readonly params = input.required<CellRendererParams<BusinessPartnerBySubRoute>>();

  readonly data = computed(() => this.params()?.data);

  readonly principalAddress = computed(() => this.data()?.principalAddress);

  // Callback from params for refresh after geo update
  readonly onGeoUpdateSuccess = computed(
    () => this.params()?.onGeoUpdateSuccess as (() => void) | undefined
  );

  // Checked state reflects geolocationPointUpdate
  readonly isRepinChecked = computed(
    () => this.principalAddress()?.geolocationPointUpdate === true
  );

  onRepinToggle(event: MatSlideToggleChange): void {
    const bpData = this.data();
    const address = this.principalAddress();
    if (!bpData || !address) return;

    const originalChecked = event.checked;
    event.source.checked = !event.checked;

    this.confirmationService
      .confirm({
        title: this.i18n.t('routing.SUB_ROUTE_PARTNERS.CELLS.REPIN_CONFIRM_TITLE'),
        description: originalChecked
          ? this.i18n.t('routing.SUB_ROUTE_PARTNERS.CELLS.REPIN_CONFIRM_ON', { address: address.address ?? '' })
          : this.i18n.t('routing.SUB_ROUTE_PARTNERS.CELLS.REPIN_CONFIRM_OFF'),
        type: ConfirmationModalType.WARNING,
        confirmButtonText: this.i18n.t('GENERAL.YES'),
        cancelButtonText: this.i18n.t('GENERAL.NO'),
        descriptionIcon: 'pin_drop',
      })
      .subscribe((confirmed) => {
        if (confirmed) {
          event.source.checked = originalChecked;
          this.performGeoUpdate(bpData._id, address._id, originalChecked);
        }
      });
  }

  private performGeoUpdate(
    businessPartnerId: string,
    addressId: string,
    geolocationPointUpdate: boolean
  ): void {
    this.bpAddressService
      .updateEmbedded(businessPartnerId, addressId, { geolocationPointUpdate })
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.handlerMessage.success({ message: this.i18n.t('routing.SUB_ROUTE_PARTNERS.CELLS.REPIN_SUCCESS') });
          this.onGeoUpdateSuccess()?.();
        },
        error: (err) => {
          this.handlerMessage.error({
            message: err.error?.message || this.i18n.t('routing.SUB_ROUTE_PARTNERS.CELLS.REPIN_ERROR'),
          });
        },
      });
  }
}
